import { COOKIE_SERVICE, LOGGER } from "@packages/common";
import type { CookieService, Logger } from "@packages/common";
import { CONFIG } from "@packages/config";
import type { Config } from "@packages/config";
import { Inject, Injectable } from "@webmotors/ioc";

import { USER_SERVICE } from "./user.types";
import type { User } from "./user.types";

@Injectable(USER_SERVICE)
export class UserService {
  private readonly logger: Logger;

  constructor(
    @Inject(COOKIE_SERVICE) private readonly cookie: CookieService,
    @Inject(LOGGER) private readonly loggerFactory: Logger,
    @Inject(CONFIG) private readonly config: Config
  ) {
    this.logger = this.loggerFactory.create(USER_SERVICE);
  }

  /**
   * Safely decodes a Base64-URL encoded string.
   * @param encodedString - Base64-URL encoded string
   * @returns Decoded string
   */
  atob(encodedString: string): string {
    try {
      const base64 = encodedString.replace(/-/g, "+").replace(/_/g, "/");
      return decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, "0")}`)
          .join("")
      );
    } catch (error) {
      this.logger.error("[ATOB] Failed to decode base64 string", { error });
      throw new Error("Failed to decode JWT.");
    }
  }

  /**
   * Parses the JWT string to retrieve the User.
   * @param encodedString - JWT token
   * @returns User object
   */
  parseJwtToken(encodedString: string): User {
    try {
      const payload = encodedString.split(".")[1];
      if (!payload) throw new Error("Invalid JWT format.");

      const jsonContent = JSON.parse(this.atob(payload));

      const user = {
        id: jsonContent["cognito:username"],
        name: jsonContent["custom:name_naturalperson"],
        email: jsonContent.email,
        phoneNumber: jsonContent.phone_number,
        authenticationToken: encodedString,
      };

      this.logger.info("[GET] Parsed User: ", user);
      return user;
    } catch (error) {
      this.logger.error("[Parse JWT] Failed to parse JWT", { error });
      throw new Error("Unable to obtain user from JWT.");
    }
  }

  /**
   * Retrieves the current user from the JWT stored in cookies.
   * @returns User or null if not found.
   */
  getCurrentUser(): User | null {
    try {
      const userEncryptedString = this.cookie.get<string>(
        this.config.authCookieName
      );

      if (!userEncryptedString) {
        this.logger.info("[GET] No user found in cookies.");
        return null;
      }

      return this.parseJwtToken(userEncryptedString);
    } catch (error) {
      this.logger.error("[GET] Failed to get user", error);
      return null;
    }
  }
}
